import { Check, TurnRightOutlined, VisibilityOutlined } from '@mui/icons-material';
import PauseIcon from '@mui/icons-material/Pause';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveOutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import EditNote from '@mui/icons-material/EditNote';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Box,
  Checkbox,
  Grid,
  Link,
  Modal,
  Skeleton,
  Stack,
  TablePagination,
  TableSortLabel,
  Typography,
} from '@mui/material';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Edit02Icon from '@untitled-ui/icons-react/build/esm/Edit02';
import loader from 'src/components/LoaderImg/loader.gif';
import {
  buildComplianceProxyNavState,
  COMPLIANCE_SIGN_ON_BEHALF_SCOPE,
  isComplianceSelectEnabled,
  isComplianceSignOnBehalfEnabled,
} from './compliance-sign-on-behalf';
import {
  COMPLIANCE_DOCUMENT_ON_BEHALF_SCOPE,
  getComplianceDocTarget,
  isComplianceEditMode,
  isComplianceUnsignMode,
} from './compliance-document-on-behalf';
import OnBehalfReasonModal from './on-behalf-reason-modal';
import { formatDateOfService } from './format-date-of-service';
import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import Label from 'src/components/Label';
import {
  complianceOnBehalf,
  dorEditCodesTreatment,
  dsnDeletePermissionTreatment,
  signOnBehalf,
} from 'src/config';
import useCurrentUser from 'src/hooks/use-current-user';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import useClockInOutButtonVisibility from 'src/hooks/use-clock-in-out-button-visibility';
import { useStopwatch } from 'src/layouts/check-in-checkout/StopwatchContext';
import ClockInOutModal from 'src/pages/therapist-dashboard/time-in-out/clock-in-out-modal';
import { paths } from 'src/paths';
import { Permissions, useScopeChecker } from 'src/rbac';
import {
  getAllSubmittedDocument,
  getEocDocumentEditDataAction,
  getTherapistInfoAction,
  getTherapistDocumentCreateEditDataAction,
  resetEocDocumentEditDataAction,
  resetSignOnBehalfAction,
  resetTherapistDocumentCreateEditDataAction,
  resetTherapistInfo,
  resetUuidAction,
  undoDocumentSignAction,
} from 'src/redux/actions/documentationAction';
import {
  deleteDailySessionNoteAction,
  resetDeleteDailySessionNote,
} from 'src/redux/actions/dailySessionNotes';
import { SignatureAction } from 'src/types/signatureContext';
import { modalStyle } from 'src/utils/common-properties';
import DeleteNoteConfirmation from '../dailySessionNotes/delete-note-confirmation';
import { checkSignPermission } from 'src/utils/signaturePermissions';
import useDialogVisibility from '../../utils/dialog-hide';
import { BootstrapDialog } from '../common-modals/BootstrapDialog';
import UndoSignConformation from '../dailySessionNotes/undo-Sign-conformation';
import { Scrollbar } from '../scrollbar';
import SucessModal from '../sucess-modal';
import {
  applyArchivedReadOnly,
  ArchivedBadge,
  archivedTitleSx,
  useArchiveNote,
} from './archive-note';
import { HeldFromBillingBadge, useHoldFromBilling } from './hold-from-billing';
import PopoverMenu from './PopoverMenu';
import ReAssignTherapist from './re-assign-therapist';
import SelectSuperVisor from './select-supervisor';
import { holdRowTestId, noteKey } from './test-ids';

type Props = {
  activeTab: string;
  requireDocuments: Array<string>;
  openPrintMode: any;
  documentationDoc: any;
  setDocumentationDoc: any;
  setIsFilterApply: any;
  abortController: any;
};

const columns: any = [
  { id: 'documentType', label: 'Document Type' },
  { id: 'dateOfService', label: 'Date of Service' },
  { id: 'therapistSignature', label: 'Therapist Signature' },
  { id: 'supervisiorSignature', label: 'Supervisior Signature' },
  { id: 'physicianSignature', label: 'Physician Signature' },
  { id: 'action', label: '', align: 'right' },
];

//  BOPS-151: the one action an archived note keeps. Used both as the item's label and as the
//  survivor test when the read-only rule is applied, so the two cannot drift apart.
const VIEW_DOCUMENT_LABEL = 'View document';

const notSortingHeader = [
  'therapistSignature',
  'supervisiorSignature',
  'physicianSignature',
  'action',
];

const SignaturePendingList: FC<Props> = (props: Props) => {
  const {
    activeTab,
    requireDocuments,
    openPrintMode,
    documentationDoc,
    setDocumentationDoc,
    setIsFilterApply,
    abortController,
  } = props;
  const {
    patients,
    patientDetails,
    episodeOfCare,
    details,
    previewDocument,
    editDocument,
    getSignaturePreview,
  } = paths;
  const navigate = useNavigate();
  const { isRunning, startStopwatch } = useStopwatch();
  const dispatch = useDispatch();
  const holdFromBilling = useHoldFromBilling();
  const { state } = useLocation();
  const [providerId, discipline] = useCurrentUser(['providerId', 'discipline']);
  const episodeOfCareId = state?.episodeOfCareId;
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const getAllSignaturePending = useSelector((state: any) => state.getAllSubmittedDocumentReducer);
  const therapistDiscipline = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.discipline
  );
  const userProviderUuid = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.providerId
  );
  const has = useScopeChecker();

  // APIT-587: clock in/out warning pop-ups (signature) gated by the per-facility
  // flag (single source of truth) instead of the enterprise flags.
  const { refetch: refetchClockInOutVisibility } = useClockInOutButtonVisibility();
  const { canShowDialog: canShowClockInDialog } = useDialogVisibility('clock_in_do_not_show');

  const [isSignOnBehalf] = useFeatureToggleWhenReady(signOnBehalf, 'ENTERPRISE');

  //  BRK-2059: the compliance on-behalf onboarding lever (same Split that surfaces the
  //  permission rows in the enterprise portal). Listing/affordance gate only — the backend
  //  authorizes by the COMPLIANCE_SIGN_ON_BEHALF grant itself.
  const [isComplianceOnBehalf] = useFeatureToggleWhenReady(complianceOnBehalf, 'ENTERPRISE');

  const [dorEditCodesEnabled] = useFeatureToggleWhenReady(dorEditCodesTreatment, 'FACILITY');

  //  THIN-7799: the Delete row needs the treatment ON as well as the permission.
  //  Fails closed — the hook returns null until the Split SDK is ready.
  const [isDsnDeleteEnabled] = useFeatureToggleWhenReady(
    dsnDeletePermissionTreatment,
    'ENTERPRISE'
  );

  //clock in modal
  const [isClockIn, setIsClockIn] = useState(false);
  const [notToShowAgain, setNotToShowAgain] = useState(false);

  const [successModal, setSuccessModal] = useState({
    success: false,
    warning: false,
  });

  const handleCloseSuccess = () => {
    setSuccessModal({
      success: false,
      warning: false,
    });
  };
  const checkoutResponse = useSelector((state: any) => state.checkoutReducer);

  const [triggerSelectSupervisor, setTriggerSupervisor] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [role, selectRole] = useState('');
  const [actionToBeContinue, setActionToBeContinue] = useState('');
  const [isProxySignature, setIsProxySignature] = useState<any>(null);

  const [getSignatureTempDoc, setSignatureTempDoc] = useState<any>('');
  // APIT-587: the document to resume editing after the clock-in popup is resolved.
  const [docForEdit, setDocForEdit] = useState<any>(null);
  const [documentPendingUnsign, setDocumentPendingUnsign] = useState<any>(null);
  //  BRK-2073: rows pending the on-behalf reason modal (unsign / edit forks).
  const [unsignOnBehalfDoc, setUnsignOnBehalfDoc] = useState<any>(null);
  const [editOnBehalfDoc, setEditOnBehalfDoc] = useState<any>(null);

  const [deleteDocument, setDeleteDocument] = useState<any>(null);
  const deleteDsnState = useSelector((state: any) => state?.deleteDailySessionNoteReducer);
  const getAssignedFacilities = useSelector((state: any) => state.getFacilityDropDownReducer);
  const facilityName =
    getAssignedFacilities?.response?.data?.data?.find(
      (fac: any) => fac?.uuid === localStorage.getItem('facilityId')
    )?.name ?? '';

  const handleConfirmDeleteNote = (reason: string) => {
    dispatch(
      deleteDailySessionNoteAction({ dsnUuid: deleteDocument?.dailySessionNoteUuid, reason })
    );
  };

  //  Re-read the list on the current page with the filters and sort the user is actually
  //  looking at. Deliberately not memoised: it is only ever called from an effect that reruns
  //  when its trigger flips, so a plain function keeps page/sort/rowPerPage current instead of
  //  capturing whatever they were when a useCallback was last rebuilt.
  const refetchSignaturePending = () => {
    dispatch(
      getAllSubmittedDocument({
        therapistUuid: providerId,
        eocId: state?.episodeOfCareId,
        signal: abortController,
        params: {
          type: activeTab,
          filter: 'PENDING',
          sortDirection: getSortOrder(sortBy),
          sortBy: sortBy,
          page: page,
          pageSize: rowPerPage,
        },
      })
    );
  };

  useEffect(() => {
    if (deleteDsnState?.isSuccess) {
      setDeleteDocument(null);
      dispatch(resetDeleteDailySessionNote());
      refetchSignaturePending();
    }
  }, [deleteDsnState?.isSuccess]);

  //  BOPS-151: refetch once an archive lands — see completed-document-list. Same reason as the
  //  delete above: archiving runs the DSN delete underneath, so the row and the count are both
  //  stale the moment it succeeds.
  const archiveNote = useArchiveNote({
    episodeOfCareId,
    onArchived: refetchSignaturePending,
  });

  const openSelectSupervisorModal = (document: any, role: string) => {
    setSelectedDocument(document);
    selectRole(role);
    setTriggerSupervisor(true);
  };

  const closeSupervisorModal = () => {
    setTriggerSupervisor(false);
  };

  const [openReassignTherapist, setOpenReassignTherapist] = useState(false);
  const openReassignTherapistModal = (document: any, role: string) => {
    setSelectedDocument(document);
    selectRole(role);
    setOpenReassignTherapist(true);
  };
  const closeReassignTherapistModal = () => {
    setOpenReassignTherapist(false);
  };

  const setPreviewDocument = (document: any) => {
    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}${previewDocument}`, {
      state: {
        document,
        episodeOfCareId,
        patientData,
        signedDocuments: document?.showAuditTrail === true,
      },
    });
  };

  //pagination logic

  const [rowPerPage, setRowPerPage] = useState(30);
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState('dateOfService');
  const [sortOrderArray, setSortArray] = useState<any>(['desc', 'desc', 'desc', 'desc', 'desc']);

  const onPageChange = (_event: any, newPage: number) => {
    dispatch(
      getAllSubmittedDocument({
        therapistUuid: providerId,
        eocId: state?.episodeOfCareId,
        signal: abortController,
        params: {
          type: activeTab,
          filter: 'PENDING',
          sortDirection: getSortOrder(sortBy),
          sortBy: sortBy,
          page: newPage,
          pageSize: rowPerPage,
        },
      })
    );
    setPage(newPage);
  };

  const getSortOrder = (sortColumn: string) => {
    switch (sortColumn) {
      case 'documentType':
        return sortOrderArray[0];
      case 'dateOfService':
        return sortOrderArray[1];
      default:
        return '';
    }
  };

  const getSortOrder1 = (sortColumn: string, newSortOrderArray: any) => {
    switch (sortColumn) {
      case 'documentType':
        return newSortOrderArray[0];
      case 'dateOfService':
        return newSortOrderArray[1];
      default:
        return '';
    }
  };

  const onRowsPerPageChange = (event: any) => {
    const newRowsPerPage = event.target.value;
    setRowPerPage(newRowsPerPage);

    dispatch(
      getAllSubmittedDocument({
        therapistUuid: providerId,
        eocId: state?.episodeOfCareId,
        signal: abortController,
        params: {
          type: activeTab,
          filter: 'PENDING',
          sortDirection: getSortOrder(sortBy),
          sortBy: sortBy,
          page: 0,
          pageSize: newRowsPerPage,
        },
      })
    );
    setPage(0);
  };
  const createSortHandler = (columnId: string, index: number) => {
    const isAsc = sortOrderArray[index] === 'asc';
    const newSortOrderArray = ['desc', 'desc', 'desc', 'desc', 'desc'];
    newSortOrderArray[index] = isAsc ? 'desc' : 'asc';

    dispatch(
      getAllSubmittedDocument({
        therapistUuid: providerId,
        eocId: state?.episodeOfCareId,
        signal: abortController,
        params: {
          type: activeTab,
          filter: 'PENDING',
          sortDirection: getSortOrder1(columnId, newSortOrderArray),
          sortBy: columnId,
          page: page,
          pageSize: rowPerPage,
        },
      })
    );
    setSortArray(newSortOrderArray);
    setSortBy(columnId);
  };

  useEffect(() => {
    if (!activeTab) return;
    setSortArray(['desc', 'desc', 'desc', 'desc', 'desc']);
    setSortBy('dateOfService');
    setPage(0);
    setRowPerPage(30);
  }, [activeTab]);

  const getSignatureTemplate = async (document: any) => {
    setSignatureTempDoc(document);
    setActionToBeContinue('GET_SIGNATURE_TEMP');
    // clock in modal show — re-fetch the per-facility flag NOW so this operation
    // uses the latest value (no page/tab refresh). Any error/404 -> false (hide).
    const canShowClockInOut = await refetchClockInOutVisibility();
    if (canShowClockInDialog() && !isRunning && !notToShowAgain && canShowClockInOut) {
      setIsClockIn(true);
      return;
    }
    //perform action here

    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}${getSignaturePreview}`, {
      state: {
        document,
        episodeOfCareId,
        patientData,
      },
    });
  };

  useEffect(() => {
    if (!checkoutResponse?.isGetCheckIn && checkoutResponse?.response === null) return;
    if (!checkoutResponse.isGetCheckIn && checkoutResponse?.response !== null) {
      setSuccessModal({
        success: false,
        warning: true,
      });
      return;
    }
    if (checkoutResponse.isGetCheckIn && checkoutResponse?.response !== null) {
      if (checkoutResponse?.clockAction === 'CLOCK_IN_SUCCESS') {
        if (actionToBeContinue === 'GET_SIGNATURE_TEMP') {
          navigate(
            `${patients}/${patientDetails}${episodeOfCare}${details}${getSignaturePreview}`,
            {
              state: {
                document: getSignatureTempDoc,
                episodeOfCareId,
                patientData,
                isProxySignature,
              },
            }
          );
          return;
        }
        if (actionToBeContinue === 'SHOW_DOCUMENT_EDIT') {
          proceedDocumentEdit(docForEdit);
          return;
        }
      }
    }
  }, [checkoutResponse]);

  // True iff the BE has issued CAN_EDIT on the supervisor axis — i.e. the current user is
  // the assigned cosigner and the doc is in a cosigner-editable state (THIN-6967).
  const isSupervisorCosignerEdit = (document: any): boolean => {
    return Boolean(
      document?.signatureContext?.allowedSupervisorSignatureActions?.includes(
        SignatureAction.CAN_EDIT
      )
    );
  };

  // Perform the actual edit navigation (both the supervisor cosigner-edit branch and
  // the normal EOC edit branch). Extracted so the clock-in resume paths can replay it.
  const proceedDocumentEdit = (document: any) => {
    // Supervisor cosigner-edit: route through the dashboard edit flow
    // (therapist-dashboarb-doc-edit.tsx → ReForm with isSupervisorEdit), which routes the
    // submit through submitReformSupervisor + the Submit & Notify Assistant modal. The EOC
    // edit route (EditDocumentPreview) doesn't support the supervisor flow.
    // BRK-2073 (K7): a compliance edit — the only source of onBehalfReason — must NEVER take
    // this fork, or the collected reason would be dropped and the edit misattributed to the
    // cosigner flow. The backend never emits both entitlements on one row (therapist-axis
    // marker+CAN_EDIT requires an unsigned doc; supervisor-axis CAN_EDIT a signed one), so
    // this guard is defense-in-depth with zero effect on legacy edits (no reason ⇒ unchanged).
    if (!document?.onBehalfReason && isSupervisorCosignerEdit(document)) {
      dispatch(resetTherapistDocumentCreateEditDataAction());
      dispatch(getTherapistDocumentCreateEditDataAction(document));
      navigate('/document/document-edit', {
        state: {
          document,
          isSupervisorEdit: true,
        },
      });
      return;
    }
    dispatch(resetEocDocumentEditDataAction());
    dispatch(
      getEocDocumentEditDataAction({
        document: document,
        episodeOfCareId,
        patientData,
        activeTab,
      })
    );
    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}${editDocument}`, {
      state: {
        document,
        episodeOfCareId,
        patientData,
        activeTab,
      },
    });
  };

  //  The pre-existing edit entry sequence (clock-in interstitial included), extracted so the
  //  BRK-2073 reason-modal confirm can replay it with the reason-augmented document.
  const continueDocumentEditFlow = async (document: any) => {
    setDocForEdit(document);
    setActionToBeContinue('SHOW_DOCUMENT_EDIT');
    // clock in modal show — re-fetch the per-facility flag NOW so this operation
    // uses the latest value (no page/tab refresh). Any error/404 -> false (hide).
    const canShowClockInOut = await refetchClockInOutVisibility();
    if (canShowClockInDialog() && !isRunning && !notToShowAgain && canShowClockInOut) {
      setIsClockIn(true);
      return;
    }
    proceedDocumentEdit(document);
  };

  const showDocumentForEdit = async (document: any) => {
    //  BRK-2073: compliance mode collects the mandatory reason BEFORE the edit opens; the reason
    //  rides the document into the persisted edit hand-off and onto the webhook mint. Reset the
    //  page-global identity lookup first so a missing providerId shows a blank card (confirm
    //  disabled) rather than a stale identity from an earlier lookup.
    if (isComplianceEditFor(document)) {
      dispatch(resetTherapistInfo());
      if (document?.providerId) {
        dispatch(getTherapistInfoAction({ providerId: document.providerId }));
      }
      setEditOnBehalfDoc(document);
      return;
    }
    await continueDocumentEditFlow(document);
  };

  const confirmEditOnBehalf = async (document: any, reason: string) => {
    setEditOnBehalfDoc(null);
    await continueDocumentEditFlow({ ...document, onBehalfReason: reason });
  };
  useEffect(() => {
    dispatch(resetUuidAction());
  }, []);

  const handleIsClock = () => {
    if (actionToBeContinue === 'GET_SIGNATURE_TEMP') {
      navigate(`${patients}/${patientDetails}${episodeOfCare}${details}${getSignaturePreview}`, {
        state: {
          document: getSignatureTempDoc,
          episodeOfCareId,
          patientData,
          isProxySignature,
        },
      });
      return;
    }
    if (actionToBeContinue === 'SHOW_DOCUMENT_EDIT') {
      proceedDocumentEdit(docForEdit);
      setIsClockIn(false);
      return;
    }
    setNotToShowAgain(true);
    setIsClockIn(false);
  };

  const checkEditPermission = (document: any) => {
    // signatureContext is the BE-owned per-user capability matrix (THIN-7160). CAN_EDIT
    // appears on the therapist axis when the current user is the form author and the
    // state still allows therapist edits, or on the supervisor axis when the current
    // user is the assigned cosigner doing a cosigner-edit (THIN-6967). Either is enough
    // to surface the Edit affordance.
    const ctx = document?.signatureContext;
    if (ctx) {
      return (
        ctx.allowedTherapistSignatureActions?.includes(SignatureAction.CAN_EDIT) ||
        ctx.allowedSupervisorSignatureActions?.includes(SignatureAction.CAN_EDIT)
      );
    }
    // Legacy fallback for rows predating signatureContext dual-emit.
    if (document.providerId === null) {
      return true;
    }
    if (document.providerId !== userProviderUuid) {
      return false;
    }

    if (document.formType?.includes('DAILY_SOAP_NOTE')) {
      return false;
    }

    if (
      document.formType?.includes('RECERTIFICATION') &&
      !has(Permissions.UPDATE_RECERTIFICATION)
    ) {
      return false;
    }
    if (document.formType?.includes('EVALUATION') && !has(Permissions.UPDATE_EVAL)) {
      return false;
    }
    return true;
  };

  const checkReadPermission = (document: any) => {
    if (document.formType?.includes('RECERTIFICATION') && !has(Permissions.READ_RECERTIFICATION)) {
      return false;
    }
    if (document.formType?.includes('EVALUATION') && !has(Permissions.READ_EVAL)) {
      return false;
    }
    return true;
  };

  const checkEditMedicalCodesPermission = (document: any) => {
    if (
      document.formType?.includes('EVALUATION') &&
      has(Permissions.EDIT_MEDICAL_CODE) &&
      dorEditCodesEnabled === true
    ) {
      return true;
    }

    return false;
  };

  const handleEditMedicalCodesClick = (document: any) => {
    navigate('/edit-medical-codes', {
      state: {
        episodeOfCareId: state.episodeOfCareId,
        episodeOfCareTitle: state.episodeOfCareTitle,
        document: document,
        patient: patientData,
      },
    });
  };

  interface SelectSupervisiorProps {
    role: string;
    document: any;
    who: string;
  }

  const SelectSupervisior: React.FC<SelectSupervisiorProps> = ({ role, document, who }) => {
    // THIN-7214: when the BE emits CAN_SELECT_* for the axis (the FSM only does so while the
    // doc is still in a DRAFT_* state, gated on author/DOR identity), wrap the assignee name
    // in a Link so the therapist/DOR can swap to a different supervisor/physician before
    // therapist-signing. After therapist sign the BE stops emitting CAN_SELECT_*, so the
    // affordance disappears automatically.
    //
    // THIN-7230: read the assignee from the per-axis field of signatureContext rather than
    // the legacy single-source document.supervisorName, which lumped whichever axis
    // populated it and made the supervisor column display the physician's name on EVAL/RECERT.
    const ctx = document?.signatureContext;
    const isSupervisorAxis = who === 'supervisor';
    const axisName = isSupervisorAxis ? ctx?.supervisor?.name : ctx?.physician?.name;
    const axisUuid = isSupervisorAxis ? ctx?.supervisor?.uuid : ctx?.physician?.uuid;
    // THIN-7246: only treat the axis as "assigned" when the BE can produce a renderable
    // name. If the uuid is set but name is null (e.g. THIN-7244 archived-physician
    // degradation), fall through to the Select-affordance branch below so the user can
    // pick a new assignee when CAN_SELECT_* is allowed — instead of rendering a blank cell.
    const axisAssigned = !!axisUuid && !!axisName;
    const axisRequired = isSupervisorAxis
      ? ctx?.supervisorSignatureRequired
      : ctx?.physicianSignatureRequired;
    //  BRK-2085: the compliance key holder gets the same Select/swap affordance, but off the
    //  dedicated CAN_COMPLIANCE_SELECT_* markers and behind the epic Split + scope — the legacy
    //  markers below stay exactly as ungated as they were for authors/DORs.
    const canReassign =
      (isSupervisorAxis
        ? ctx?.allowedSupervisorSignatureActions?.includes(SignatureAction.CAN_SELECT_SUPERVISOR)
        : ctx?.allowedPhysicianSignatureActions?.includes(SignatureAction.CAN_SELECT_PHYSICIAN)) ||
      isComplianceSelectEnabled({
        complianceSplitOn: isComplianceOnBehalf,
        hasComplianceScope: has(COMPLIANCE_SIGN_ON_BEHALF_SCOPE),
        document,
        axis: isSupervisorAxis ? 'supervisor' : 'physician',
      });
    return (
      <TableCell>
        {axisAssigned ? (
          <>
            {canReassign ? (
              <Link
                sx={{ cursor: 'pointer' }}
                onClick={() => openSelectSupervisorModal(document, role)}
              >
                <Typography variant="caption">{axisName}</Typography>
              </Link>
            ) : (
              <Typography variant="caption">{axisName}</Typography>
            )}

            {/* THIN-7204: read per-axis status from signatureContext rather than the legacy
                supervisorStatus DTO field (slated for removal in THIN-7205). */}
            {role === 'Supervisor' &&
              who === 'supervisor' &&
              (document?.signatureContext?.supervisor?.signStatus === 'COMPLETED' ||
              document?.status === 'PENDING' ? (
                <Label
                  backgroundColor="#F790091F"
                  color="#B54708"
                  text={
                    (document?.signatureContext?.supervisor?.signStatus?.slice(0, 1) ?? '') +
                    (document?.signatureContext?.supervisor?.signStatus?.slice(1).toLowerCase() ??
                      '')
                  }
                />
              ) : document?.signatureContext?.allowedSupervisorSignatureActions?.includes(
                  SignatureAction.CAN_SIGN
                ) ? (
                <Link
                  display={'inline'}
                  onClick={() => getSignatureTemplate(document)}
                >
                  <Typography variant="body2">Sign document</Typography>
                </Link>
              ) : (
                <Label
                  backgroundColor="#F790091F"
                  color="#B54708"
                  text={'Pending'}
                />
              ))}
          </>
        ) : (
          (() => {
            // No assignee yet — derive Select / Pending / Not-Required from signatureContext.
            // The BE's CAN_SELECT_* action factors in author identity, DOR role, and supervision
            // permissions, so the FE no longer needs to combine checkSupervisorPermission +
            // supervisorStatus + providerId + SIGN_ON_BEHALF predicates.
            if (!axisRequired) {
              return <Typography variant="caption">Not Required</Typography>;
            }
            if (canReassign) {
              return (
                <Link
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openSelectSupervisorModal(document, role)}
                >
                  Select {role}
                </Link>
              );
            }
            return <Typography variant="caption">Pending to be Assigned</Typography>;
          })()
        )}
      </TableCell>
    );
  };

  const checkWhosSignNeed = (document: any) => {
    // THIN-7238: chain each dereference so null/undefined document or documentType
    // doesn't blow up the call (.toLowerCase() on undefined was the latent throw).
    const type = document?.documentType?.toLowerCase() ?? '';
    if (type.includes('evaluation') || type.includes('recertification')) {
      return 'Physician';
    }
    return 'Supervisor';
  };

  const HandleStartClock = () => {
    const millageInfo = {
      locationType: 'BUILDING',
      payByMilage: null,
    };
    startStopwatch('MILLAGE', millageInfo);
  };

  const onSelectAllClickPendingSign = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsFilterApply(false);
    if (event.target.checked) {
      setDocumentationDoc((prev: any) => ({
        ...prev,
        signaturePending: requireDocuments,
      }));
      return;
    }
    setDocumentationDoc((prev: any) => ({
      ...prev,
      signaturePending: [],
    }));
  };

  const checkUndoPermission = (document: any): boolean => {
    // signatureContext-driven: BE reports CAN_UNSIGN on whichever axis the current user
    // owns (therapist on the doc they authored, supervisor on the doc they cosigned,
    // physician on a locked doc, or any privileged unsigner on a physician-signed doc).
    const ctx = document?.signatureContext;
    if (!ctx) return false;
    return Boolean(
      ctx.allowedTherapistSignatureActions?.includes(SignatureAction.CAN_UNSIGN) ||
        ctx.allowedSupervisorSignatureActions?.includes(SignatureAction.CAN_UNSIGN) ||
        ctx.allowedPhysicianSignatureActions?.includes(SignatureAction.CAN_UNSIGN)
    );
  };

  const removedSignDocument = (document: any) => {
    //  BRK-2073: compliance mode swaps the plain confirmation for the mandatory reason+consent
    //  modal. The target's identity card needs the same lookup the sign modal uses. Reset first:
    //  the reducer is page-global, so a lookup from an earlier action (e.g. sign-on-behalf)
    //  would otherwise show the WRONG identity if this row's target id is missing — the modal
    //  fails closed on a blank identity instead.
    if (isComplianceUnsignFor(document)) {
      dispatch(resetTherapistInfo());
      const target = getComplianceDocTarget(document);
      if (target?.onBehalfOfProviderId) {
        dispatch(getTherapistInfoAction({ providerId: target.onBehalfOfProviderId }));
      }
      setUnsignOnBehalfDoc(document);
      return;
    }
    setDocumentPendingUnsign(document);
  };

  const confirmUnsignOnBehalf = (document: any, reason: string) => {
    //  Same FSM-aware endpoint as the legacy confirm below; onBehalfReason is the BRK-2072
    //  discriminator that makes the backend verify the grant and stamp provenance on the
    //  unsign audit rows.
    dispatch(
      undoDocumentSignAction({
        formSubmissionUuid: document.uuid,
        documentType: document?.documentType?.replaceAll(' ', '_')?.toUpperCase(),
        onBehalfReason: reason,
      })
    );
    setUnsignOnBehalfDoc(null);
  };

  const confirmUnsignDocument = () => {
    if (!documentPendingUnsign) return;
    const document = documentPendingUnsign;
    // THIN-7258: always route through the FSM-aware unsign endpoint. The
    // legacy editSignDocAction → /provider/document/edit-signed-document
    // endpoint is a deprecated no-op on the BE (DocumentationServiceImpl.
    // editSignedDocument logs a warning and returns immediately). The
    // unified FSM handles both reform-signed and legacy Jotform-signed
    // docs via deriveFromColumns lazy state hydration (THIN-7164).
    dispatch(
      undoDocumentSignAction({
        formSubmissionUuid: document.uuid,
        documentType: document?.documentType?.replaceAll(' ', '_')?.toUpperCase(),
      })
    );
    setDocumentPendingUnsign(null);
  };

  const handleNavigateToDailySessionNote = (document: any) => {
    const selectedPatientData = {
      name: patientData?.name,
      uuid: patientData?.uuid,
      firstName: patientData?.firstName,
      lastName: patientData?.lastName,
      notesId: patientData?.dailySessionNoteUuid,
      discipline: document?.type,
    };
    navigate('/daily-session-notes', {
      state: {
        document: {
          dateOfService: document?.dateOfService,
          source: 'FROM_EOC',
          dailySessionNoteUuid: document?.dailySessionNoteUuid,
          patientEoc: selectedPatientData,
        },
      },
    });
  };
  const handleSignOnBehalf = async (document: any) => {
    dispatch(resetSignOnBehalfAction());
    setSignatureTempDoc(document);
    setActionToBeContinue('GET_SIGNATURE_TEMP');
    setIsProxySignature('PROXY_SIGNATURE');
    // clock in modal show — re-fetch the per-facility flag NOW so this operation
    // uses the latest value (no page/tab refresh). Any error/404 -> false (hide).
    const canShowClockInOut = await refetchClockInOutVisibility();
    if (canShowClockInDialog() && !isRunning && !notToShowAgain && canShowClockInOut) {
      setIsClockIn(true);
      return;
    }
    //perform action here

    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}${getSignaturePreview}`, {
      state: {
        document,
        episodeOfCareId,
        patientData,
        isProxySignature: 'PROXY_SIGNATURE',
      },
    });
  };

  //  BRK-2059: compliance on-behalf — permission-driven, role/discipline-independent, both
  //  signable axes. Deliberately NOT discipline-gated (cross-discipline coverage is the point).
  const isComplianceProxyEnable = (document: any): boolean =>
    isComplianceSignOnBehalfEnabled({
      complianceSplitOn: isComplianceOnBehalf,
      hasComplianceScope: has(COMPLIANCE_SIGN_ON_BEHALF_SCOPE),
      document,
    });

  //  BRK-2073: document on-behalf (unsign + edit) — same single Split, its own scope, and the
  //  backend's per-row CAN_COMPLIANCE_DOCUMENT_ON_BEHALF marker. The marker never appears for a
  //  natively-entitled actor (author / assigned supervisor / DOR-without-the-key), so the legacy
  //  branches below stay byte-identical for everyone else.
  const isComplianceUnsignFor = (document: any): boolean =>
    isComplianceUnsignMode({
      complianceSplitOn: isComplianceOnBehalf,
      hasDocumentScope: has(COMPLIANCE_DOCUMENT_ON_BEHALF_SCOPE),
      document,
    });

  const isComplianceEditFor = (document: any): boolean =>
    isComplianceEditMode({
      complianceSplitOn: isComplianceOnBehalf,
      hasDocumentScope: has(COMPLIANCE_DOCUMENT_ON_BEHALF_SCOPE),
      document,
    });

  const handleComplianceSignOnBehalf = async (document: any) => {
    dispatch(resetSignOnBehalfAction());
    setSignatureTempDoc(document);
    setActionToBeContinue('GET_SIGNATURE_TEMP');
    const proxyNavState = buildComplianceProxyNavState(document);
    setIsProxySignature(proxyNavState);
    //  Same clock-in interstitial dance as every other signing entry point; the resume paths
    //  carry `isProxySignature` state through, typed object included.
    const canShowClockInOut = await refetchClockInOutVisibility();
    if (canShowClockInDialog() && !isRunning && !notToShowAgain && canShowClockInOut) {
      setIsClockIn(true);
      return;
    }

    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}${getSignaturePreview}`, {
      state: {
        document,
        episodeOfCareId,
        patientData,
        isProxySignature: proxyNavState,
      },
    });
  };

  const isSignOnBehalfEnable = (document: any): boolean => {
    // signatureContext-driven: the BE computes CAN_SIGN_ON_BEHALF for current user against
    // current state (DOR-only, therapist-axis-only, only when therapist hasn't signed).
    // We still gate by discipline and the FE-side SIGN_ON_BEHALF permission flag because
    // those are FE-routing concerns (which dashboard / which role tabs this user sees).
    const ctx = document?.signatureContext;
    return (
      document.type === discipline &&
      has(Permissions.SIGN_ON_BEHALF) &&
      Boolean(ctx?.allowedTherapistSignatureActions?.includes(SignatureAction.CAN_SIGN_ON_BEHALF))
    );
  };

  return (
    <>
      {deleteDsnState?.loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
          }}
        >
          <img
            src={loader}
            alt="Deleting..."
            style={{ height: 80 }}
          />
        </Box>
      )}
      <Box
        // sx={{ border: '1px solid #F2F4F7', borderRadius: '20px' }}
        p={3}
      >
        <Card>
          <Grid
            container
            sx={{ padding: '18px 24px 18px 24px', position: 'relative' }}
          >
            <Typography variant="h6">Signature Pending</Typography>

            <Box
              sx={{
                border: '1px solid rgba(0, 0, 0, 0.26)',
                borderRadius: '12px',
                padding: '3px',
                display: 'flex',
                alignItems: 'center',
                marginLeft: '12px',
              }}
            >
              <Typography sx={{ color: '#111927', fontSize: '13px', letterSpacing: '0.16px' }}>
                {`${
                  getAllSignaturePending?.response?.totalElements
                    ? getAllSignaturePending?.response?.totalElements
                    : 0
                } Document`}
              </Typography>
            </Box>
          </Grid>
          <Divider />
          <Scrollbar>
            <Table sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  {columns?.map((column: any, index: any) => (
                    <TableCell
                      key={column.id}
                      align={column?.align}
                    >
                      {!notSortingHeader.includes(column?.id) ? (
                        <>
                          {openPrintMode && column?.id === 'documentType' ? (
                            <Checkbox
                              color="primary"
                              indeterminate={
                                documentationDoc?.signaturePending?.length > 0 &&
                                documentationDoc?.signaturePending?.length <
                                  requireDocuments?.length
                              }
                              checked={
                                requireDocuments?.length > 0 &&
                                documentationDoc?.signaturePending?.length ===
                                  requireDocuments?.length
                              }
                              onChange={onSelectAllClickPendingSign}
                              inputProps={{
                                'aria-label': 'select all desserts',
                              }}
                            />
                          ) : null}
                          <TableSortLabel
                            active={true}
                            direction={sortOrderArray[index]}
                            onClick={() => createSortHandler(column.id, index)}
                          >
                            {column.label}
                          </TableSortLabel>
                        </>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {getAllSignaturePending?.loader ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Box
                        textAlign="center"
                        p={3}
                      >
                        <Skeleton animation="wave" />
                        <Skeleton animation="wave" />
                        <Skeleton animation="wave" />
                        <Skeleton animation="wave" />
                        <Skeleton animation="wave" />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : getAllSignaturePending?.response?.totalElements ? (
                  // THIN-7238: filter null/undefined entries so a single bad row doesn't
                  // crash the whole table render via `document.documentType` etc.
                  requireDocuments?.filter(Boolean).map((document: any) => (
                    <TableRow
                      key={document.uuid}
                      sx={{
                        backgroundColor: documentationDoc?.signaturePending?.some(
                          (item: any) => item?.uuid === document?.uuid
                        )
                          ? '#EBEEFE'
                          : null,
                      }}
                      onClick={() => {
                        if (openPrintMode) {
                          setIsFilterApply(false);
                          const chipIndex = documentationDoc?.signaturePending?.find(
                            (chip: any) => chip?.uuid === document?.uuid
                          );
                          if (chipIndex) {
                            const updatedChips = documentationDoc?.signaturePending?.filter(
                              (chip: any) => chip?.uuid !== document?.uuid
                            );

                            setDocumentationDoc((prev: any) => ({
                              ...prev,
                              signaturePending: updatedChips,
                            }));
                            return;
                          } else {
                            setDocumentationDoc((prev: any) => ({
                              ...prev,
                              signaturePending: [
                                ...(documentationDoc?.signaturePending ?? []),
                                document,
                              ],
                            }));
                          }
                          return;
                        }
                      }}
                    >
                      <TableCell>
                        <Grid
                          container
                          alignItems="center"
                          direction="row"
                        >
                          {openPrintMode ? (
                            <Checkbox
                              checked={documentationDoc?.signaturePending?.some(
                                (item: any) => item?.uuid === document?.uuid
                              )}
                            />
                          ) : null}
                          {/*  BOPS-151: an archived note reads as struck through, matching the
                              Archived Documents table below. Gated with the badge, not on its
                              own: BOPS-173 says a user without the permission must not learn a
                              note was archived, and a struck-through title would say it. */}
                          <Typography
                            variant="subtitle2"
                            sx={{
                              marginLeft: '4px',
                              ...(archiveNote.isArchived(document) ? archivedTitleSx : {}),
                            }}
                          >
                            {document.documentType}
                          </Typography>
                          {holdFromBilling.isHeldFromBilling(document) && (
                            <HeldFromBillingBadge origin={holdFromBilling.holdOrigin(document)} />
                          )}
                          {archiveNote.isArchived(document) && <ArchivedBadge />}
                        </Grid>
                      </TableCell>
                      <TableCell>
                        {formatDateOfService(document.dateOfService, 'dd MMM, yyyy') || '-'}
                      </TableCell>
                      <TableCell
                        sx={{
                          cursor: checkSignPermission(document).value ? 'pointer' : 'default',
                        }}
                      >
                        <Typography
                          variant="caption"
                          display={'inline'}
                        >
                          {document.signedBy ? (
                            document.signedBy[0]
                          ) : (
                            <Link onClick={() => openReassignTherapistModal(document, role)}>
                              <Typography
                                variant="body2"
                                sx={{ cursor: 'pointer' }}
                              >
                                Re-assign Therapist
                              </Typography>
                            </Link>
                          )}
                        </Typography>
                        {checkSignPermission(document).value ? (
                          <Link
                            display={'inline'}
                            onClick={() => getSignatureTemplate(document)}
                          >
                            <Typography variant="body2">
                              {checkSignPermission(document).label}
                            </Typography>
                          </Link>
                        ) : checkSignPermission(document).label === 'Signed' ? (
                          <Label
                            backgroundColor="#15B79E1F"
                            color="#107569"
                            text="Signed"
                          />
                        ) : (
                          <Label
                            backgroundColor="#F790091F"
                            color="#B54708"
                            text="Pending"
                          />
                        )}
                        {document.signedBy &&
                          document?.status !== 'COMPLETED' &&
                          document?.formType !== 'DAILY_SOAP_NOTE' && (
                            <Link onClick={() => openReassignTherapistModal(document, role)}>
                              <Typography
                                variant="body2"
                                sx={{ cursor: 'pointer' }}
                              >
                                Re-assign Therapist
                              </Typography>
                            </Link>
                          )}
                      </TableCell>
                      <TableCell>
                        <SelectSupervisior
                          role={'Supervisor'}
                          document={document}
                          who={'supervisor'}
                        />
                      </TableCell>
                      <TableCell>
                        <SelectSupervisior
                          role={'Physician'}
                          document={document}
                          who={'physician'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <PopoverMenu
                          testId={holdRowTestId('note', noteKey(document), 'menu')}
                          //  BOPS-151: strip the menu to View document once the note is archived.
                          //  Completed Documents applies the same helper to its own menu.
                          items={applyArchivedReadOnly(
                            [
                              {
                                label: VIEW_DOCUMENT_LABEL,
                                onClick: () => setPreviewDocument(document),
                                icon: <VisibilityOutlined fontSize="small" />,
                                disabled: !document.pdfUrl || !checkReadPermission(document),
                              },
                              {
                                label: 'Edit document',
                                onClick: () => showDocumentForEdit(document),
                                icon: <Edit02Icon fontSize="small" />,
                                // Action-list-driven: checkEditPermission reads CAN_EDIT from
                                // signatureContext on either axis, and the BE only emits it when
                                // the current state actually permits the event. The discipline
                                // gate stays as a UI filter (PT supervisor can't edit OT notes).
                                //  BRK-2073: compliance mode is deliberately cross-discipline —
                                //  the discipline gate stays for everyone else.
                                disabled: !(
                                  (therapistDiscipline === document?.type ||
                                    isComplianceEditFor(document)) &&
                                  checkEditPermission(document)
                                ),
                              },
                              {
                                label: 'Sign document',
                                onClick: () => getSignatureTemplate(document),
                                icon: <EditNote fontSize="small" />,
                                disabled: !(
                                  document?.signatureContext?.allowedTherapistSignatureActions?.includes(
                                    SignatureAction.CAN_SIGN
                                  ) ||
                                  document?.signatureContext?.allowedSupervisorSignatureActions?.includes(
                                    SignatureAction.CAN_SIGN
                                  ) ||
                                  document?.signatureContext?.allowedPhysicianSignatureActions?.includes(
                                    SignatureAction.CAN_SIGN
                                  )
                                ),
                              },
                              //  Legacy DOR flow first (unchanged); otherwise the compliance
                              //  flow (BRK-2059) — permission-driven, cross-discipline, and the
                              //  only path to the supervisor-axis cosign. One evaluation feeds
                              //  both onClick and disabled so they can never desync.
                              ...(() => {
                                const legacyProxyEnabled =
                                  Boolean(isSignOnBehalf) && isSignOnBehalfEnable(document);
                                const complianceProxyEnabled = isComplianceProxyEnable(document);
                                return [
                                  {
                                    label: 'Sign on Behalf',
                                    onClick: () =>
                                      legacyProxyEnabled
                                        ? handleSignOnBehalf(document)
                                        : handleComplianceSignOnBehalf(document),
                                    icon: <EditNote fontSize="small" />,
                                    disabled: !(legacyProxyEnabled || complianceProxyEnabled),
                                  },
                                ];
                              })(),
                              {
                                label: 'Edit ICD-10 Codes',
                                onClick: () => handleEditMedicalCodesClick(document),
                                icon: <Edit02Icon fontSize="small" />,
                                disabled: !checkEditMedicalCodesPermission(document),
                              },
                              {
                                label: 'Go to Session Notes',
                                onClick: () => handleNavigateToDailySessionNote(document),
                                icon: <TurnRightOutlined fontSize="small" />,
                                disabled:
                                  !document?.formType?.includes(
                                    'DAILY_SOAP_NOTE',
                                    'DAILY_SOAP_NOTE_REFORM'
                                  ) || document?.providerId !== providerId,
                              },

                              {
                                label: 'Select Supervisor',
                                onClick: () => openSelectSupervisorModal(document, 'Supervisor'),
                                icon: <SupervisorAccountIcon fontSize="small" />,
                                //  BRK-2085 follow-up (epic audit): both entry points to the
                                //  picker — this kebab item and the inline cell link — honor the
                                //  compliance markers with the same Split+scope gate.
                                disabled: !(
                                  document?.signatureContext?.allowedSupervisorSignatureActions?.includes(
                                    SignatureAction.CAN_SELECT_SUPERVISOR
                                  ) ||
                                  isComplianceSelectEnabled({
                                    complianceSplitOn: isComplianceOnBehalf,
                                    hasComplianceScope: has(COMPLIANCE_SIGN_ON_BEHALF_SCOPE),
                                    document,
                                    axis: 'supervisor',
                                  })
                                ),
                              },
                              {
                                label: 'Select Physician',
                                onClick: () => openSelectSupervisorModal(document, 'Physician'),
                                icon: <LocalHospitalIcon fontSize="small" />,
                                disabled: !(
                                  document?.signatureContext?.allowedPhysicianSignatureActions?.includes(
                                    SignatureAction.CAN_SELECT_PHYSICIAN
                                  ) ||
                                  isComplianceSelectEnabled({
                                    complianceSplitOn: isComplianceOnBehalf,
                                    hasComplianceScope: has(COMPLIANCE_SIGN_ON_BEHALF_SCOPE),
                                    document,
                                    axis: 'physician',
                                  })
                                ),
                              },
                              {
                                label: 'Re-assign Therapist',
                                onClick: () => openReassignTherapistModal(document, role),
                                icon: <SwapHorizIcon fontSize="small" />,
                                // signatureContext.allowedTherapistSignatureActions has CAN_REASSIGN
                                // when therapist signature is unsigned and current user is the
                                // original author or a DOR. DAILY_SOAP_NOTE is excluded as a
                                // FE-only routing rule (SOAP notes have a separate reassignment
                                // flow that doesn't use this menu).
                                disabled: !(
                                  document?.signatureContext?.allowedTherapistSignatureActions?.includes(
                                    SignatureAction.CAN_REASSIGN
                                  ) && document?.formType !== 'DAILY_SOAP_NOTE'
                                ),
                              },
                              {
                                label: 'Undo Signature',
                                onClick: () => removedSignDocument(document),
                                icon: <RestoreOutlinedIcon fontSize="small" />,
                                disabled: !checkUndoPermission(document),
                              },
                              {
                                label: 'Delete',
                                onClick: () => setDeleteDocument(document),
                                icon: (
                                  <DeleteIcon
                                    fontSize="small"
                                    sx={{ color: 'error.main' }}
                                  />
                                ),
                                sx: { color: 'error.main' },
                                //  THIN-7799: gate on the treatment, so the whole DSN hard-delete
                                //  feature can be turned off per enterprise.
                                //  BOPS-446: no permission check here — `deletable` is the server's
                                //  answer for this user (Any key, Own key on notes they wrote, closed
                                //  month), so an own-only user sees Delete on their own notes. Only an
                                //  explicit true shows it: with no permission check behind it, an absent
                                //  flag would offer a hard delete to everyone.
                                disabled:
                                  !isDsnDeleteEnabled ||
                                  document?.formType !== 'DAILY_SOAP_NOTE_REFORM' ||
                                  document?.deletable !== true,
                              },
                              {
                                //  BOPS-151/173/174: sits with Delete, the other irreversible
                                //  action. Hidden — never greyed — when the toggle is off, the
                                //  permission is missing, the note produced no PDF, or the month
                                //  is closed out; useArchiveNote owns that whole rule. No divider
                                //  here: unlike Completed Documents this menu keeps the billing
                                //  actions below Delete, so a rule would split the wrong pair.
                                label: 'Archive',
                                onClick: () => archiveNote.requestArchive(document),
                                icon: (
                                  <ArchiveOutlinedIcon
                                    fontSize="small"
                                    sx={{ color: 'error.main' }}
                                  />
                                ),
                                sx: { color: 'error.main' },
                                //  Every sibling carries `disabled` because the array below is
                                //  filtered on it; Archive's whole rule lives in `hidden`.
                                disabled: false,
                                hidden: !archiveNote.canArchive(document),
                              },
                              {
                                label: 'Hold from billing',
                                testId: holdRowTestId('note', noteKey(document), 'hold'),
                                onClick: () => holdFromBilling.requestHold(document),
                                icon: (
                                  <PauseIcon
                                    fontSize="small"
                                    sx={{ color: 'warning.dark' }}
                                  />
                                ),
                                sx: { color: 'warning.dark' },
                                //  THIN-8138: offered even inside a closed period, then refused
                                //  with an explanation — see `isHoldBlockedByClosedPeriod`.
                                disabled: !(
                                  holdFromBilling.canHold(document) ||
                                  holdFromBilling.isHoldBlockedByClosedPeriod(document)
                                ),
                              },
                              {
                                label: 'Include in billing',
                                testId: holdRowTestId('note', noteKey(document), 'include'),
                                onClick: () => holdFromBilling.includeInBilling(document),
                                icon: (
                                  <Check
                                    fontSize="small"
                                    sx={{ color: 'success.main' }}
                                  />
                                ),
                                //  THIN-8138: offered even inside a closed period, then refused
                                //  with an explanation — see `isIncludeBlockedByClosedPeriod`.
                                disabled: !(
                                  holdFromBilling.canInclude(document) ||
                                  holdFromBilling.isIncludeBlockedByClosedPeriod(document)
                                ),
                              },
                            ].filter((item) => !item.disabled),
                            archiveNote.isReadOnly(document),
                            VIEW_DOCUMENT_LABEL
                          )}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Box
                        textAlign="center"
                        p={3}
                      >
                        <Typography
                          color={'text.secondary'}
                          variant="subtitle2"
                        >
                          No Records Found
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Scrollbar>
          <TablePagination
            component="div"
            count={getAllSignaturePending?.response?.totalElements || 0}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            page={page}
            rowsPerPage={rowPerPage}
            rowsPerPageOptions={[30, 50, 100]}
          />
        </Card>
      </Box>
      {/* clock in modal */}
      {canShowClockInDialog() && (
        <BootstrapDialog
          PaperProps={{ style: { maxWidth: '750px' } }}
          onClose={() => setIsClockIn(false)}
          aria-labelledby="customized-dialog-title"
          open={isClockIn && !notToShowAgain}
        >
          <ClockInOutModal
            close={handleIsClock}
            handleClockIn={HandleStartClock}
          />
        </BootstrapDialog>
      )}

      {/* success modal */}

      <Modal open={successModal?.success || successModal?.warning}>
        <SucessModal
          close={handleCloseSuccess}
          isSucess={successModal.success}
          title={successModal?.success ? 'Clock In successfully' : 'Clock in Failed'}
          message=""
        />
      </Modal>

      {/* For selecting a supervisor */}
      <Modal
        style={modalStyle}
        open={triggerSelectSupervisor}
        onClose={closeSupervisorModal}
      >
        <div>
          <SelectSuperVisor
            activeTab={activeTab}
            close={closeSupervisorModal}
            selectedDocument={selectedDocument}
            role={role}
          />
        </div>
      </Modal>

      {/* For re-assign therapist */}
      <Modal
        style={modalStyle}
        open={openReassignTherapist}
        onClose={closeReassignTherapistModal}
      >
        <div>
          <ReAssignTherapist
            activeTab={activeTab}
            close={closeReassignTherapistModal}
            selectedDocument={selectedDocument}
            role="Therapist"
          />
        </div>
      </Modal>

      {/* Unsign confirmation */}
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '750px' } }}
        onClose={() => setDocumentPendingUnsign(null)}
        open={Boolean(documentPendingUnsign)}
      >
        <UndoSignConformation
          close={() => setDocumentPendingUnsign(null)}
          confirm={confirmUnsignDocument}
          title="Undo Signature"
          message="Are you sure you want to unsign this document? You will need to re-sign it if you confirm."
        />
      </BootstrapDialog>

      {/* BRK-2073: unsign on behalf — mandatory reason + consents */}
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '750px' } }}
        onClose={() => setUnsignOnBehalfDoc(null)}
        open={Boolean(unsignOnBehalfDoc)}
      >
        {unsignOnBehalfDoc && (
          <OnBehalfReasonModal
            document={unsignOnBehalfDoc}
            onClose={() => setUnsignOnBehalfDoc(null)}
            onConfirm={confirmUnsignOnBehalf}
            title="Unsigning on behalf of"
            actionPhrase="unsigning this document"
            onBehalfOfLabel={
              getComplianceDocTarget(unsignOnBehalfDoc)?.axis === 'SUPERVISOR'
                ? 'Supervisor'
                : 'Therapist'
            }
            confirmLabel="Unsign"
          />
        )}
      </BootstrapDialog>

      {/* BRK-2073: edit on behalf — reason collected before the editor opens */}
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '750px' } }}
        onClose={() => setEditOnBehalfDoc(null)}
        open={Boolean(editOnBehalfDoc)}
      >
        {editOnBehalfDoc && (
          <OnBehalfReasonModal
            document={editOnBehalfDoc}
            onClose={() => setEditOnBehalfDoc(null)}
            onConfirm={confirmEditOnBehalf}
            title="Editing on behalf of"
            actionPhrase="editing this document"
            onBehalfOfLabel="Therapist"
            confirmLabel="Continue to edit"
          />
        )}
      </BootstrapDialog>

      {/* Delete DSN confirmation */}
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '520px' } }}
        onClose={() => setDeleteDocument(null)}
        aria-labelledby="delete-note-dialog-title"
        open={Boolean(deleteDocument)}
      >
        <DeleteNoteConfirmation
          close={() => setDeleteDocument(null)}
          confirm={handleConfirmDeleteNote}
          patientName={`${patientData?.lastName ?? ''} ${patientData?.firstName ?? ''}`.trim()}
          dateOfService={formatDateOfService(deleteDocument?.dateOfService, 'MMMM d, yyyy')}
          loading={deleteDsnState?.loading}
          therapistName={deleteDocument?.signedBy ? deleteDocument?.signedBy[0] : ''}
          facility={facilityName}
        />
      </BootstrapDialog>
      {holdFromBilling.holdConfirmationDialog}
      {holdFromBilling.closedPeriodDialog}
      {archiveNote.archiveConfirmationDialog}
    </>
  );
};

export default SignaturePendingList;
